import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Project {
  id: number;
  title: string;
  description?: string;
  image: string;
  homepage_image_url?: string;
  video?: string;
  additionalImages?: string[];
  additionalVideos?: string[];
  client?: string;
  category?: string;
  year?: string;
  featured?: boolean;
  createdAt?: string;
}

interface SiteContent {
  ticker_text?: string;
  ticker_speed?: string;
  ticker_font_size?: string;
  ticker_height?: string;
  hero_modal_video_url?: string;
  hero_video_url?: string;
  ticker_button_enabled?: string;
  ticker_button_text?: string;
  ticker_font_family?: string;
  intro_text?: string;
  intro_text_enabled?: string;
}

interface CustomFont {
  font_family: string;
  font_display_name: string;
  woff2_url?: string;
  woff_url?: string;
  ttf_url?: string;
  otf_url?: string;
}

export default function ProjectsSection() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [content, setContent] = useState<SiteContent>();
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const navigate = useNavigate();

  // Mock data as fallback with reliable images
  const mockProjects: Project[] = [
    {
      id: 1,
      title: 'BRAND IDENTITY',
      image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 2,
      title: 'CREATIVE CAMPAIGN',
      video: 'https://player.vimeo.com/external/434045526.sd.mp4?s=c27eecc69a27dbc4ff2b87d38afc35f1a9e7c02d&profile_id=165',
      image: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 3,
      title: 'WEBSITE DESIGN',
      image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 4,
      title: 'PACKAGING DESIGN',
      image: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 5,
      title: 'EDITORIAL DESIGN',
      video: 'https://player.vimeo.com/external/371433846.sd.mp4?s=236da2f3c0fd273d2c6d9a064f3ae35579b2bbdf&profile_id=165',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 6,
      title: 'DIGITAL EXPERIENCE',
      image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 7,
      title: 'MOTION GRAPHICS',
      video: 'https://player.vimeo.com/external/434045526.sd.mp4?s=c27eecc69a27dbc4ff2b87d38afc35f1a9e7c02d&profile_id=165',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 8,
      title: 'PRODUCT DESIGN',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 9,
      title: 'RETAIL EXPERIENCE',
      image: 'https://images.unsplash.com/photo-1493612276216-ee3925520721?w=700&h=700&fit=crop&crop=center'
    },
    {
      id: 10,
      title: 'ART DIRECTION',
      video: 'https://player.vimeo.com/external/371433846.sd.mp4?s=236da2f3c0fd273d2c6d9a064f3ae35579b2bbdf&profile_id=165',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&h=700&fit=crop&crop=center'
    }
  ];

  const defaultTickerText = 'CREATIVE WORK • DESIGN EXCELLENCE • VISUAL STORYTELLING • BRAND INNOVATION •';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch projects from public API (no authentication required)
        const projectsResponse = await fetch('/admin/public-api.php?action=get_projects');

        console.log('Projects API Response Status:', projectsResponse.status);
        console.log('Projects API Response Headers:', projectsResponse.headers.get('content-type'));

        // Check if response is actually JSON
        const contentType = projectsResponse.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn('API returned non-JSON response, using mock data');
          setProjects(mockProjects);
          setLoading(false);
          return;
        }

        const projectsData = await projectsResponse.json();
        console.log('Projects API Response Data:', projectsData);

        // Fetch content
        const contentResponse = await fetch('/admin/content-api.php');
        const contentData = await contentResponse.json();

        if (projectsData.success && projectsData.data && projectsData.data.length > 0) {
          // Map database fields to component interface
          const mappedProjects = projectsData.data.map((project: any) => ({
            id: project.id,
            title: project.title,
            description: project.description,
            image: project.image_url,
            homepage_image_url: project.homepage_image_url,
            video: project.video_url,
            client: project.client_name,
            category: project.category,
            year: project.project_year,
            featured: project.featured
          }));
          setProjects(mappedProjects);
          console.log('Successfully loaded projects from database:', mappedProjects);
          console.log('First project image URLs:', {
            image_url: mappedProjects[0]?.image,
            homepage_image_url: mappedProjects[0]?.homepage_image_url
          });
        } else {
          console.log('No projects found in database or API failed, using mock data');
          console.log('API Response:', projectsData);
          setProjects(mockProjects);
        }

        if (contentData.success) {
          setContent(contentData.data);
          
          // Fetch custom fonts if available
          if (contentData.custom_fonts) {
            setCustomFonts(contentData.custom_fonts);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        console.log('Using mock data due to API error');
        setProjects(mockProjects);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Enhanced font loading with Safari compatibility for ticker
  useEffect(() => {
    const loadTickerFont = async () => {
      try {
        // Load custom fonts first
        if (customFonts.length > 0) {
          const style = document.createElement('style');
          let cssRules = '';
          
          customFonts.forEach(font => {
            const sources = [];
            if (font.woff2_url) sources.push(`url('${font.woff2_url}') format('woff2')`);
            if (font.woff_url) sources.push(`url('${font.woff_url}') format('woff')`);
            if (font.ttf_url) sources.push(`url('${font.ttf_url}') format('truetype')`);
            if (font.otf_url) sources.push(`url('${font.otf_url}') format('opentype')`);
            
            if (sources.length > 0) {
              cssRules += `
                @font-face {
                  font-family: '${font.font_family}';
                  src: ${sources.join(', ')};
                  font-display: swap;
                  font-weight: normal;
                  font-style: normal;
                }
              `;
            }
          });
          
          if (cssRules) {
            style.textContent = cssRules;
            document.head.appendChild(style);
            console.log('Custom fonts loaded for ticker:', customFonts.map(f => f.font_family));
          }
        }

        // Load Google Font for ticker if needed
        if (content?.ticker_font_family && content.ticker_font_family !== 'Inter' && !customFonts.find(f => f.font_family === content.ticker_font_family)) {
          const fontName = content.ticker_font_family;
          const fontNameForUrl = fontName.replace(/\s+/g, '+');
          const existingLink = document.querySelector(`link[href*="${fontNameForUrl}"]`);
          
          if (!existingLink) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${fontNameForUrl}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
            link.rel = 'stylesheet';
            link.crossOrigin = 'anonymous';
            
            // Safari-specific loading handling
            let resolved = false;
            
            link.onload = () => {
              if (!resolved) {
                resolved = true;
                console.log(`Ticker Google Font loaded successfully: ${fontName}`);
              }
            };
            
            link.onerror = () => {
              if (!resolved) {
                resolved = true;
                console.warn(`Failed to load ticker Google Font: ${fontName}`);
              }
            };
            
            document.head.appendChild(link);
            
            // Safari fallback timeout
            setTimeout(() => {
              if (!resolved) {
                resolved = true;
                console.warn(`Ticker font loading timeout for: ${fontName}`);
              }
            }, 4000);

            // Enhanced Safari font loading check
            if ('fonts' in document) {
              try {
                await document.fonts.load(`16px "${fontName}"`);
                console.log(`Ticker font face verified: ${fontName}`);
              } catch (error) {
                console.warn(`Ticker font face verification failed for ${fontName}:`, error);
              }
            }
          }
        }
        
        setFontsLoaded(true);
      } catch (error) {
        console.error('Error loading ticker font:', error);
        setFontsLoaded(true);
      }
    };

    if (!loading && content) {
      loadTickerFont();
    } else if (!loading) {
      setFontsLoaded(true);
    }
  }, [content, customFonts, loading]);

  // Handle image loading errors
  const handleImageError = (projectId: number) => {
    console.log(`Image failed to load for project ${projectId}`);
    setImageErrors(prev => new Set(prev).add(projectId));
  };

  // Get image URL with proper database image priority
  const getImageUrl = (project: Project, index: number) => {
    // Function to validate and process URL
    const processImageUrl = (url: string | null | undefined): string | null => {
      if (!url || url.trim() === '' || url === 'null' || url === 'undefined') {
        return null;
      }

      const cleanUrl = url.trim();

      // If it's already a complete URL, return it
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
        console.log(`Using complete URL for project ${project.id}:`, cleanUrl);
        return cleanUrl;
      }

      // If it's a relative path, construct the full URL with domain prefix
      if (cleanUrl.startsWith('/')) {
        const fullUrl = 'https://watookzoukunnen.nl' + cleanUrl;
        console.log(`Constructed URL for project ${project.id}:`, fullUrl);
        return fullUrl;
      } else {
        const fullUrl = 'https://watookzoukunnen.nl/' + cleanUrl;
        console.log(`Constructed URL for project ${project.id}:`, fullUrl);
        return fullUrl;
      }
    };

    // Priority 1: Use homepage-specific image if available
    const homepageImageUrl = processImageUrl(project.homepage_image_url);
    if (homepageImageUrl) {
      console.log(`Using homepage image for project ${project.id}:`, homepageImageUrl);
      return homepageImageUrl;
    }

    // Priority 2: Use main project image if available
    const mainImageUrl = processImageUrl(project.image);
    if (mainImageUrl) {
      console.log(`Using main image for project ${project.id}:`, mainImageUrl);
      return mainImageUrl;
    }

    // Priority 3: If no database images available, return null to show placeholder
    console.log(`No valid database image found for project ${project.id}, showing placeholder`);
    return null;
  };

  // Define random positioning and stacking for each project
  const getProjectStyle = (index: number) => {
    const positions = [
      { transform: 'translate(0px, 0px)', zIndex: 50 },
      { transform: 'translate(80px, -40px)', zIndex: 49 },
      { transform: 'translate(-60px, -80px)', zIndex: 48 },
      { transform: 'translate(-40px, 50px)', zIndex: 47 },
      { transform: 'translate(40px, -120px)', zIndex: 46 },
      { transform: 'translate(60px, -100px)', zIndex: 45 },
      { transform: 'translate(-80px, 60px)', zIndex: 44 },
      { transform: 'translate(100px, -160px)', zIndex: 43 },
      { transform: 'translate(-100px, -40px)', zIndex: 42 },
      { transform: 'translate(20px, 80px)', zIndex: 41 }
    ];

    return positions[index % positions.length] || positions[0];
  };

  // Fixed navigation line – replaced template literal with string concatenation to avoid syntax issues
  const handleProjectClick = (projectId: number) => {
    navigate('/project/' + projectId);
  };

  // Function to calculate responsive text size based on text length
  const getResponsiveTextSize = (text: string, isMobile: boolean = false) => {
    const textLength = text.length;

    if (isMobile) {
      // Mobile responsive sizing
      if (textLength <= 10) return 'text-2xl sm:text-3xl';
      if (textLength <= 15) return 'text-xl sm:text-2xl';
      if (textLength <= 20) return 'text-lg sm:text-xl';
      return 'text-base sm:text-lg';
    } else {
      // Desktop responsive sizing
      if (textLength <= 10) return 'text-4xl xl:text-5xl';
      if (textLength <= 15) return 'text-3xl xl:text-4xl';
      if (textLength <= 20) return 'text-2xl xl:text-3xl';
      return 'text-xl xl:text-2xl';
    }
  };

  // Modal handling
  const openVideoModal = () => {
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  // Function to check if URL is a Vimeo embed URL
  const isVimeoEmbed = (url: string) => {
    return url.includes('player.vimeo.com');
  };

  // Function to prepare Vimeo URL for modal (with controls)
  const prepareVimeoUrlForModal = (url: string) => {
    if (!isVimeoEmbed(url)) return url;

    // Add autoplay but keep controls for modal
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}autoplay=1&controls=1`;
  };

  // Enhanced font style with Safari fallbacks for ticker
  const getTickerFontStyle = () => {
    const fontName = content?.ticker_font_family || 'Archivo';
    
    if (!fontName || fontName === 'Archivo') {
      return { fontFamily: 'Archivo, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 900 };
    }
    
    // Check if it's a custom font
    const isCustomFont = customFonts.find(f => f.font_family === fontName);
    
    if (isCustomFont) {
      return { 
        fontFamily: `"${fontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`,
        fontWeight: 900
      };
    }
    
    // Google Font with Safari fallbacks
    return { 
      fontFamily: `"${fontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`,
      fontWeight: 900
    };
  };

  if (loading || !fontsLoaded) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading projects...</p>
          </div>
        </div>
      </section>
    );
  }

  const tickerText = content?.ticker_text || defaultTickerText;
  const tickerSpeed = content?.ticker_speed || '20';
  const tickerFontSize = content?.ticker_font_size || 'text-6xl md:text-8xl';
  const tickerHeight = content?.ticker_height || 'py-8';
  const isButtonEnabled = content?.ticker_button_enabled === '1';
  const buttonText = content?.ticker_button_text || 'Watch Our Story';

  return (
    <section className="py-16 bg-white overflow-hidden">
      {/* Bold Ticker Tape */}
      <div className={`bg-black text-white ${tickerHeight} mb-20 overflow-hidden ticker-container`}>
        <div
          className="ticker-content whitespace-nowrap"
          style={{
            animationDuration: `${tickerSpeed}s`
          }}
        >
          <span 
            className={`${tickerFontSize} font-black uppercase tracking-wider`}
            style={getTickerFontStyle()}
          >
            {tickerText} &nbsp;&nbsp;&nbsp;&nbsp; {tickerText} &nbsp;&nbsp;&nbsp;&nbsp; {tickerText} &nbsp;&nbsp;&nbsp;&nbsp; {tickerText}
          </span>
        </div>
      </div>

      {/* Intro Text Section */}
      {content?.intro_text_enabled === '1' && content?.intro_text && (
        <div className="max-w-4xl mx-auto px-8 text-center mb-20 -mt-10">
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed" style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 400 }}>
            {content.intro_text}
          </p>
        </div>
      )}

      {/* Ticker Button */}
      {isButtonEnabled && (content?.hero_modal_video_url || content?.hero_video_url) && (
        <div className="text-center -mt-10 mb-20">
          <button
            onClick={openVideoModal}
            className="bg-black text-white px-8 py-4 text-lg hover:bg-gray-800 transition-all duration-300 cursor-pointer whitespace-nowrap rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105"
            style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 600 }}
          >
            <i className="ri-play-fill mr-2"></i>
            {buttonText}
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-8 lg:px-5">
        {/* Desktop: Grid container with 2 columns and overlapping */}
        <div className="hidden lg:grid grid-cols-2 gap-20 relative pb-40" style={{ minHeight: `${Math.ceil(projects.length / 2) * 500 + 200}px` }}>
          {/* Left Column */}
          <div className="relative">
            {projects.filter((_, index) => index % 2 === 0).map((project, index) => {
              const actualIndex = index * 2;
              const style = getProjectStyle(actualIndex);
              const imageUrl = getImageUrl(project, actualIndex);

              return (
                <div
                  key={project.id}
                  className="group absolute cursor-pointer transition-all duration-500 hover:scale-105"
                  style={{
                    ...style,
                    top: `${index * 500 + 100}px`
                  }}
                  onClick={() => handleProjectClick(project.id)}
                >
                  {/* Square tile */}
                  <div className="aspect-square w-[500px] h-[500px] relative overflow-hidden shadow-2xl">
                    {/* Media Container */}
                    <div className="w-full h-full relative bg-gray-200">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={() => handleImageError(project.id)}
                          onLoad={() => console.log(`Image loaded successfully for project ${project.id}:`, imageUrl)}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <i className="ri-image-line text-gray-400 text-4xl"></i>
                        </div>
                      )}
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent group-hover:from-black/95 group-hover:via-black/50 transition-all duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-8 xl:p-12">
                        <h3 className={`text-white font-black tracking-[0.05em] xl:tracking-[0.1em] uppercase leading-tight break-words ${getResponsiveTextSize(project.title)}`} style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 800 }}>
                          {project.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Column */}
          <div className="relative">
            {projects.filter((_, index) => index % 2 === 1).map((project, index) => {
              const actualIndex = index * 2 + 1;
              const style = getProjectStyle(actualIndex);
              const imageUrl = getImageUrl(project, actualIndex);

              return (
                <div
                  key={project.id}
                  className="group absolute cursor-pointer transition-all duration-500 hover:scale-105"
                  style={{
                    ...style,
                    top: `${index * 500 + 180}px`
                  }}
                  onClick={() => handleProjectClick(project.id)}
                >
                  {/* Square tile */}
                  <div className="aspect-square w-[500px] h-[500px] relative overflow-hidden shadow-2xl">
                    {/* Media Container */}
                    <div className="w-full h-full relative bg-gray-200">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={() => handleImageError(project.id)}
                          onLoad={() => console.log(`Image loaded successfully for project ${project.id}:`, imageUrl)}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <i className="ri-image-line text-gray-400 text-4xl"></i>
                        </div>
                      )}
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent group-hover:from-black/95 group-hover:via-black/50 transition-all duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-8 xl:p-12">
                        <h3 className={`text-white font-black tracking-[0.05em] xl:tracking-[0.1em] uppercase leading-tight break-words ${getResponsiveTextSize(project.title)}`} style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 800 }}>
                          {project.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Simple vertical stack */}
        <div className="lg:hidden space-y-8 pb-20">
          {projects.map((project, index) => {
            const imageUrl = getImageUrl(project, index);

            return (
              <div
                key={project.id}
                className="group cursor-pointer transition-all duration-500 hover:scale-105"
                onClick={() => handleProjectClick(project.id)}
              >
                {/* Mobile tile */}
                <div className="aspect-square w-full max-w-md mx-auto relative overflow-hidden shadow-2xl rounded-lg">
                  {/* Media Container */}
                  <div className="w-full h-full relative bg-gray-200">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        onError={() => handleImageError(project.id)}
                        onLoad={() => console.log(`Image loaded successfully for project ${project.id}:`, imageUrl)}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <i className="ri-image-line text-gray-400 text-4xl"></i>
                      </div>
                    )}
                  </div>

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent group-hover:from-black/95 group-hover:via-black/50 transition-all duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                      <h3 className={`text-white font-black tracking-[0.05em] sm:tracking-[0.1em] uppercase leading-tight break-words ${getResponsiveTextSize(project.title, true)}`} style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 800 }}>
                        {project.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-20">
          {/* Removed the project count message */}
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
          <div className="relative w-full max-w-6xl aspect-video">
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute -top-12 right-0 w-12 h-12 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/50 transition-all duration-300 cursor-pointer z-10"
            >
              <i className="ri-close-line text-white text-2xl"></i>
            </button>

            {/* Video Content */}
            {(() => {
              const modalVideoUrl = content?.hero_modal_video_url || content?.hero_video_url;

              if (modalVideoUrl && isVimeoEmbed(modalVideoUrl)) {
                return (
                  <iframe
                    src={prepareVimeoUrlForModal(modalVideoUrl)}
                    className="w-full h-full rounded-lg"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                );
              } else if (modalVideoUrl) {
                return (
                  <video className="w-full h-full rounded-lg" controls autoPlay muted>
                    <source src={modalVideoUrl} type="video/mp4" />
                  </video>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}
    </section>
  );
}
